import "pinia-plugin-persistedstate";
import { HTTPError } from "ky";
import { getCurrency } from "locale-currency";
import { defineStore } from "pinia";
import type { ConversionRateResponse } from "shared-core/types/currency";
import type {
  CompanyProfile,
  PublicRiskAnalysis,
} from "shared-core/types/risk-analysis";
import { getSuperjson } from "shared-ui/composables/use-superjson.ts";
import { formatDuration } from "shared-ui/utils/formatters.ts";
import type { ComposerTranslation } from "vue-i18n";
import { useApi } from "../utils/api.ts";
import { sleep } from "../utils/sleep.ts";

const DEFAULT_SEARCHES = 3;

export type CurrencyConversion = { rate: number; currency: string };

export const useRiskAnalysisStore = defineStore("risk-analysis-store", {
  state: (): {
    searchesLeft: number;
    analysis: PublicRiskAnalysis | null;
    currencyConversion: CurrencyConversion;
    companyProfile: CompanyProfile | null;
    requireEmail: boolean;
    abortController: AbortController | null;
  } => ({
    searchesLeft: DEFAULT_SEARCHES,
    analysis: null,
    currencyConversion: { rate: 1, currency: "USD" },
    companyProfile: null,
    requireEmail: false,
    abortController: null,
  }),
  persist: {
    storage: sessionStorage,
    pick: ["analysis", "currencyConversion", "requireEmail"],
  },
  actions: {
    async analyzeDomain(domain: string, t: ComposerTranslation): Promise<void> {
      this.analysis = null;
      this.companyProfile = null;
      this.abortController = new AbortController();
      const superjson = await getSuperjson();

      let riskAnalysisData: PublicRiskAnalysis;
      try {
        const riskAnalysisResponse = await useApi().get<
          PublicRiskAnalysis | CompanyProfile
        >(`public/risk-analysis?domain=${domain}`, {
          parseJson: (text) => superjson.parse(text),
          retry: 0,
          signal: this.abortController.signal,
        });
        if (riskAnalysisResponse.headers.has("ratelimit-remaining")) {
          const rateLimitRemaining = riskAnalysisResponse.headers.get(
            "ratelimit-remaining",
          );
          this.searchesLeft = rateLimitRemaining
            ? parseInt(rateLimitRemaining)
            : DEFAULT_SEARCHES;
        }
        if (riskAnalysisResponse.responseStatus === 200) {
          riskAnalysisData =
            (await riskAnalysisResponse.data) as PublicRiskAnalysis;
        } else {
          this.companyProfile =
            (await riskAnalysisResponse.data) as CompanyProfile;
          riskAnalysisData = await this.pollRiskAnalysisData(
            domain,
            this.abortController.signal,
          );
        }
      } catch (error) {
        this.analysis = null;

        let errorMessage: string;
        if (error instanceof HTTPError && error.response.status === 404) {
          errorMessage = error.message;
        } else if (
          error instanceof HTTPError &&
          error.response.status === 429
        ) {
          this.searchesLeft = 0;
          errorMessage = `You have reached your reports limit. Please try again in ${await formatDuration(parseInt(error.response.headers.get("retry-after") ?? "3600"), t)}`;
        } else {
          errorMessage = "We seem to have some issues, please try again later";
        }

        console.error(error);

        throw new Error(errorMessage);
      } finally {
        this.abortController = null;
      }

      this.analysis = {
        ...riskAnalysisData,
      };
    },

    async fetchCachedDomainResult(domain: string): Promise<PublicRiskAnalysis> {
      const superjson = await getSuperjson();

      this.analysis = await (
        await useApi().get<PublicRiskAnalysis>(
          `public/risk-analysis/cached?domain=${domain}`,
          {
            parseJson: (text) => superjson.parse(text),
          },
        )
      ).data;

      return this.analysis;
    },

    async pollRiskAnalysisData(
      domain: string,
      signal: AbortSignal,
    ): Promise<PublicRiskAnalysis> {
      const maxAttempts = 60;
      const superjson = await getSuperjson();

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const riskAnalysisResponse = await useApi().get<PublicRiskAnalysis>(
          `public/risk-analysis/cached?domain=${domain}`,
          {
            parseJson: (text) => superjson.parse(text),
            throwHttpErrors: false,
            signal,
          },
        );

        if (riskAnalysisResponse.responseStatus === 200) {
          return await riskAnalysisResponse.data;
        } else {
          await sleep(1000, signal);
        }
      }

      throw new Error(
        `Failed to poll Risk Analysis data after ${maxAttempts} attempts`,
      );
    },

    async fetchConversionRate(locale: string): Promise<void> {
      const currency: string = getCurrency(locale) ?? "USD";

      // If we already have conversion rate for current currency, no need to fetch
      if (this.currencyConversion.currency === currency) {
        return;
      }

      const response = await useApi().get<ConversionRateResponse>(
        `currency/conversion-rate?currency=${currency}`,
      );

      this.currencyConversion = {
        currency: currency,
        rate: (await response.data).rate,
      };
    },

    abortRiskAnalysis(): void {
      this.abortController?.abort();
    },

    setRequireEmail(require: boolean): void {
      this.requireEmail = require;
    },
  },
});
